<template>
  <div class="topbar">
    <div class="header" :class="{ warning: !isOnline }">
      <div class="left">
        <router-link class="logo" :to="{ name: 'dashboard' }" @click="$store.dispatch('resume.resetSearch')">
          <WashiLogo />
        </router-link>

        <div
          v-if="route.includes('editor') && loggedIn && currentResume"
          v-clickout="
            () => {
              (breadcrumWorkspaceIsOpen = null), (breadcrumGroupIsOpen = null);
            }
          "
          class="breadcrumb"
        >
          <div
            class="openable workspace"
            :class="{ opened: breadcrumWorkspaceIsOpen }"
            @click="breadcrumWorkspaceIsOpen = !breadcrumWorkspaceIsOpen"
          >
            <div
              class="title"
              :class="{
                'uppercase-first': currentResume.workspaceType !== 1 || breadcrumWorkspaceIsOpen,
              }"
            >
              {{ breadcrumWorkspaceIsOpen ? $t('editor.topbar.goTo') : breadcrumWorkspace }}
            </div>
            <ArrowIcon class="arrow" />
            <div v-if="breadcrumWorkspaceIsOpen" class="dropdown left-dropdown">
              <div v-if="user.organization" @click="() => goToWorkspace(1)">
                {{ user.organization.name }}
              </div>
              <div class="uppercase-first" @click="() => goToWorkspace(2)">
                {{ $t('workspaces._2') }}
              </div>
              <div v-if="metrics.sharedCount" class="uppercase-first" @click="() => goToWorkspace(3)">
                {{ $t('workspaces._3') }}
              </div>
            </div>
          </div>
          <div>
            <div class="title">{{ currentResume.candidate.firstname }}</div>
          </div>
          <div
            v-if="resumeInGroup.length > 1"
            class="openable"
            :class="{ opened: breadcrumGroupIsOpen }"
            @click="breadcrumGroupIsOpen = !breadcrumGroupIsOpen"
          >
            <div class="title">
              {{ cleanJobTitle(currentResume.candidate.jobTitle) }}
            </div>
            <ArrowIcon class="arrow" />
            <div v-if="breadcrumGroupIsOpen" class="dropdown right-dropdown">
              <div
                v-for="r in resumeInGroup.filter(re => re.id !== currentResume.id)"
                :key="r.id"
                @click="() => goToResume(r.id)"
              >
                {{ cleanJobTitle(r.jobTitle) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="right">
        <div v-if="route.includes('editor')" class="infos">
          <div v-if="!isOnline">{{ $t('topBar.offlineWarning') }}</div>
          <div v-else-if="canEdit" class="info">
            {{ $t(`${savingResume}`) }}
          </div>
          <div v-else-if="!canEdit" class="info">
            {{ $t('editor.messages.readOnly') }}
          </div>
        </div>

        <div v-if="route.includes('editor') && !!user.organization" class="container" @click="openTranslationPopup">
          <TranslateLogo class="icon" />
          <span class="text">{{ $t('topBar.translate') }}</span>
        </div>

        <div
          v-if="route.includes('editor') && canEdit"
          class="container design"
          :class="{ active: currentMode === 'design', disabled: !user }"
          @click="toggleDesignMode"
        >
          <painting v-if="currentMode === 'design'" class="icon" />
          <pen v-else class="icon" />
          <div class="text">
            {{ currentMode !== 'design' ? $t('topBar.designMode') : $t('topBar.editorMode') }}
          </div>
        </div>

        <div v-if="route.includes('editor') && loggedIn" class="container share" @click.stop="share">
          <share class="icon" />
          <div class="text">{{ $t('topBar.sharing') }}</div>
        </div>
        <div v-if="route.includes('editor') && loggedIn" class="container duplicate" @click.stop="duplicate">
          <DuplicateIcon class="icon" />
          <div class="text">{{ $t('topBar.duplicate') }}</div>
        </div>

        <div
          v-if="route.includes('editor')"
          class="container export"
          @click="docxAvailable ? (exportTypeIsOpen = !exportTypeIsOpen) : openExportPopup('pdf')"
        >
          <arrowShare class="icon" />
          <div v-if="!downloading" class="export-button">
            <div class="text">{{ $t('topBar.export') }}</div>
            <ArrowIcon v-if="docxAvailable" class="arrow" :class="{ opened: exportTypeIsOpen }" />
          </div>
          <div v-else class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div v-show="exportTypeIsOpen" class="dropdown">
            <div @click="openExportPopup('pdf')">.pdf</div>
            <div @click="openExportPopup('docx')">.docx</div>
          </div>
        </div>

        <div class="help-icon" @click="toggleHelpPopup">
          <HelpIcon />
        </div>

        <div class="container profil" @click="$router.push({ name: loggedIn ? 'profile' : 'register' })">
          <manProfile class="icon" />
          <div class="text">{{ $t(loggedIn ? 'topBar.profil' : 'topBar.signup') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import TranslateLogo from '@/assets/icons/translate.svg';
import WashiLogo from '@/assets/Logo_Washi_White.svg';
import ArrowIcon from '@/assets/listing/arrow_accordion.svg';
import arrowShare from '@/assets/listing/arrow_share.svg';
import painting from '@/assets/listing/pen.svg';
import pen from '@/assets/listing/painting.svg';
import manProfile from '@/assets/listing/man profile.svg';
import share from '@/assets/listing/share.svg';
import DuplicateIcon from '@/assets/listing/sheet.svg';
import HelpIcon from '@/assets/icons/help.svg';

export default {
  name: 'TopbarComponent',
  components: {
    WashiLogo,
    ArrowIcon,
    manProfile,
    painting,
    share,
    arrowShare,
    DuplicateIcon,
    pen,
    HelpIcon,
    TranslateLogo,
  },
  data: function () {
    return {
      downloading: false,
      breadcrumWorkspaceIsOpen: false,
      breadcrumGroupIsOpen: false,
      exportTypeIsOpen: false,
      resumeInGroup: [],
      isOnline: true,
    };
  },
  computed: {
    ...mapGetters(['currentResume', 'currentMode', 'user', 'savingResume', 'metrics', 'loggedIn']),
    route() {
      return this.$route.fullPath;
    },
    resume() {
      return this.currentResume;
    },
    canEdit() {
      if (!this.resume) return false;
      return this.resume.permissions === 0 || this.resume.permissions === 2;
    },
    breadcrumWorkspace() {
      if (!this.currentResume) {
        return '';
      }
      if (this.currentResume.workspaceType === 1) {
        return this.user.organization ? this.user.organization.name : '';
      }
      return this.$t(`workspaces._${this.currentResume.workspaceType}`);
    },
    docxAvailable() {
      return this.resume?.template?.docxAvailable;
    },
  },
  watch: {
    async resume(resume) {
      if (this.loggedIn && resume) {
        this.resumeInGroup = await this.$store.dispatch('resumes.getGroup', resume.id);
      }
    },
  },
  async mounted() {
    if (this.resume && this.loggedIn) {
      this.resumeInGroup = await this.$store.dispatch('resumes.getGroup', this.resume.id);
    }

    window.addEventListener('online', this.updateOnlineStatus);
    window.addEventListener('offline', this.updateOnlineStatus);
  },
  methods: {
    toggleHelpPopup() {
      this.$store.dispatch('setPopup', {
        popupType: 'CustomerServicePopup',
      });
    },
    updateOnlineStatus() {
      this.isOnline = navigator.onLine;
    },

    async openTranslationPopup() {
      try {
        const translatedResume = await this.$store.dispatch('setPopup', {
          popupType: 'TranslatePopup',
          popupData: this.currentResume,
        });

        const id = translatedResume.id;
        if (id) {
          await this.$router.push({ name: 'editor', params: { id } });
        }
      } catch (e) {
        this.$store.dispatch('toaster/translationFailed', { root: true });
      }
    },

    toggleDesignMode() {
      if (this.currentMode === 'design') {
        this.$store.dispatch('resumes.setMode', {
          currentMode: this.canEdit ? 'editor' : 'preview',
        });
      } else {
        this.$store.dispatch('resumes.setMode', { currentMode: 'design' });
      }
    },
    goToWorkspace(workspaceType) {
      this.$router.push({
        name: 'dashboard',
        query: { workspace: workspaceType },
      });
    },
    goToResume(resumeId) {
      this.$router.push({ name: 'editor', params: { id: resumeId } });
    },
    async logout() {
      await this.$store.dispatch('auth.logout');
      this.$router.push({ name: 'home' });
    },
    async openExportPopup(fileType) {
      if (this.downloading) return;
      this.downloading = true;

      try {
        let file;
        switch (fileType) {
          case 'docx':
            file = await this.$store.dispatch('resumes.download_docx', this.$route.params.id);
            break;
          case 'pdf':
          default:
            file = await this.$store.dispatch('resumes.download_pdf', this.$route.params.id);
            break;
        }
        const blob = new Blob([file]);
        const link = URL.createObjectURL(blob);

        const div = document.createElement('div');
        div.innerHTML = this.currentResume.candidate.jobTitle;
        const cleanedJobTitle = div.textContent || div.innerText || '';

        const fileTitle = [
          this.currentResume.owner.organization?.name,
          this.currentResume.candidate.firstname,
          cleanedJobTitle,
        ]
          .filter(Boolean)
          .join(' - ');

        this.$store.dispatch('setPopup', {
          popupType: 'ExportPopup',
          popupData: {
            link,
            fileTitle,
            userOrganization: this.currentResume.owner.organization,
            fileType,
          },
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.downloading = false;
      }
    },
    async share() {
      try {
        const result = await this.$store.dispatch('setPopup', {
          popupType: 'ShareResume',
          popupData: this.currentResume,
        });
        console.log(result);
      } catch (e) {
        console.error(e);
      }
    },
    async duplicate() {
      this.editMenu = null;
      try {
        const id = (
          await this.$store.dispatch('setPopup', {
            popupType: 'DuplicateResume',
            popupData: this.currentResume,
          })
        ).id;
        if (id) {
          await this.$router.push({ name: 'editor', params: { id } });
        }
      } catch (e) {
        console.error(e);
      }
    },
    cleanJobTitle(jobTitle) {
      const div = document.createElement('div');
      div.innerHTML = jobTitle;
      return div.textContent || div.innerText || '';
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive/topbar.scss';

$layout-breakpoint-small: 512px;

.topbar {
  position: fixed;
  z-index: 100;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: app.$topbar-height;
  width: 100%;
  background: #f2f2f2;
  border-bottom: solid 1px #dadada;
  box-sizing: border-box;

  .breadcrumb {
    display: flex;
    color: white;

    .workspace {
      min-width: 160px;
      align-items: center;
    }

    & > div {
      position: relative;
      padding: 2px 8px 2px 12px;
      min-width: 84px;

      border: solid 1px white;
      border-right: 0;
      border-left: 0;

      &::before {
        content: '';
        position: absolute;
        z-index: 5;

        right: -6px;
        top: -1px;
        bottom: -1px;
        width: 12px;
        background: white;
        clip-path: polygon(0 0, 50% 0, 100% 50%, 50% calc(100% + 1px), 0 calc(100% + 1px), 50% 50%);
      }

      &::after {
        content: '';
        position: absolute;
        z-index: 6;
        right: -6px;
        top: -1px;
        bottom: -1px;
        width: 12px;
        background: colors.$light-blue-color;
        clip-path: polygon(
          1px 0,
          calc(50% - 1px) 0,
          calc(100% - 1px) 50%,
          calc(50% - 1px) calc(100% + 1px),
          1px calc(100% + 1px),
          calc(50% + 1px) 50%
        );
      }

      &:first-child {
        padding-left: 8px;
        border-left: solid 1px white;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      &:last-child {
        padding-right: 6px;
        border-right: solid 1px white;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        z-index: 4;

        &::before {
          content: none;
        }

        &::after {
          content: none;
        }
      }

      .title {
        max-width: 256px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      &.openable {
        cursor: pointer;
        position: relative;
        user-select: none;
        justify-content: space-between;
        display: flex;

        .arrow {
          margin: -6px -8px -8px;
          transition: 0.2s ease-in-out;
        }

        &.opened {
          background-color: white;
          color: colors.$light-blue-color;

          .arrow {
            transform: rotate(180deg);
          }

          &:first-child::before {
            clip-path: polygon(0 0, 50% 0, 100% 50%, 50% calc(100% + 1px), 0 calc(100% + 1px), 0 50%);
          }
        }

        .dropdown {
          position: absolute;
          background-color: white;
          border-radius: 4px;
          border: solid 1px colors.$light-blue-color;
          color: colors.$light-blue-color;
          left: 0;
          top: calc(100% + 2px);

          &.right-dropdown {
            left: -4px;
            right: -2px;
            padding-left: 4px;
          }

          &.left-dropdown {
            left: -2px;
            right: 2px;
          }

          & > div {
            border-bottom: solid 1px colors.$light-blue-color;
            padding: 2px 8px;

            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &:hover {
              background-color: rgba(colors.$light-blue-color, 0.33);
            }

            &:last-child {
              border-bottom: none;
            }
          }
        }
      }
    }
  }
  .header {
    position: fixed;
    z-index: 100;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: app.$topbar-height;
    padding: 0 16px;
    width: 100%;
    background: colors.$light-blue-color;
    box-sizing: border-box;
    &.warning {
      background-color: colors.$red-color;
      .breadcrumb > div:after {
        background-color: colors.$red-color;
      }
    }

    .left,
    .right {
      height: 100%;
      display: flex;
      align-items: center;

      .logo {
        margin-right: 16px;
      }

      .help-icon {
        cursor: pointer;
        transition: all 0.1s ease-in-out;
        margin: 0 0 0 1rem;
        height: 24.5px;
        width: 24.5px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:active {
          scale: 0.9;
        }

        &:hover {
          background: rgba(202, 202, 202, 0.219);
          border-radius: 3px;
          cursor: pointer;
        }

        svg {
          width: 20px;
          height: 20px;
          color: white;
        }
      }

      .icon {
        width: 16px;
        height: 16px;
      }
      .back-button {
        display: flex;
        justify-content: center;
        align-items: center;
        text-transform: uppercase;
        color: white;
        border-radius: 3px;
        padding: 4px 6px;
        background-color: colors.$main-color;
        font-weight: bold;
        font-size: 11px;
        letter-spacing: 1.3px;
        cursor: pointer;
        .icon {
          width: 9px;
          margin-left: 6px;
        }
      }
    }
    .button {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 8px;
      cursor: pointer;
      background-color: white;
      color: colors.$light-blue-color;
      border-radius: 3px;
      box-sizing: border-box;
      text-transform: uppercase;
      font-size: 11px;
      letter-spacing: 1.3px;
      font-weight: bold;
      height: 20px;
      user-select: none;
      transition: box-shadow 0.3s;
      .icon {
        color: #2d6470;
        width: 30px;
        height: 30px;

        &.revert {
          transform: scaleX(-1);
        }
      }

      &:not(.active):not(.disabled):hover {
        box-shadow: 0 0 0 4px rgba(202, 202, 202, 0.219);
      }

      &.active {
        color: black;
        background-color: colors.$secondary-color;
      }
      &.disabled {
        color: black;
        background-color: grey;
        pointer-events: none;
        cursor: not-allowed;
      }

      &:last-child {
        margin-right: 0;
      }
    }

    .buttons-group {
      display: flex;
      align-items: center;
      .info {
        font-size: 12px;
        color: white;
        margin: 0 8px;
        text-decoration: underline;
      }
    }

    .container {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 14px;
      color: #ffffff;
      border-radius: 3px;
      transition: background-color 0.3s;
      padding: 5px;

      &:hover {
        background: rgba(202, 202, 202, 0.219);
        border-radius: 3px;
        cursor: pointer;
      }

      .icon {
        margin-right: 7px;
        width: 14px;
        height: 14px;
      }

      .text {
        font-size: 12px;
        font-weight: 900;
        letter-spacing: 1.42px;
        text-transform: uppercase;
      }

      &.design {
        color: colors.$white-color;
        &.active {
          background-color: colors.$dark-blue-color;
          padding: 5px;
          border-radius: 3px;
        }
      }

      &.export {
        position: relative;

        .dropdown {
          position: absolute;
          display: flex;
          flex-direction: column;
          background-color: white;
          border-radius: 4px;
          border: solid 1px colors.$light-blue-color;
          color: colors.$light-blue-color;
          left: 0;
          top: calc(100% + 2px);
          right: -2px;

          & > div {
            border-bottom: solid 1px colors.$light-blue-color;
            padding: 2px 8px;

            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &:hover {
              background-color: rgba(colors.$light-blue-color, 0.33);
            }

            &:last-child {
              border-bottom: none;
            }
          }
        }

        .export-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          position: relative;
          height: 15px;
          overflow: hidden;

          .arrow {
            transition: all 0.4s ease-in-out;
            rotate: 0deg;
            &.opened {
              rotate: 180deg;
            }
          }
        }
      }
    }

    .infos {
      display: flex;
      align-items: center;
      height: 100%;
      color: #ffffff;
      font-size: 11px;
      text-decoration: underline;
      margin-right: 16px;
    }
  }
}
@media screen and (max-width: $layout-breakpoint-small) {
  .container {
    margin-top: 3px !important;
  }
  .container .text {
    margin-top: 6px !important;
  }
}
</style>
